'use client';

import { useCallback, useState } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { Field } from '../../../components/field';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Tooltip } from '../../../components/tooltip';
import { toasterApi } from '../../toast/ToastProvider';
import { Tick01Icon, Copy01Icon, ViewIcon, ViewOffIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

export type InternalNetworkSensitiveReadonlyFieldProps = {
  label: string;
  value: string;
  copyAriaLabel: string;
  showAriaLabel: string;
  hideAriaLabel: string;
};

export function InternalNetworkSensitiveReadonlyField({
  label,
  value,
  copyAriaLabel,
  showAriaLabel,
  hideAriaLabel,
}: InternalNetworkSensitiveReadonlyFieldProps) {
  const tAdmin = useTranslations(
    'designSystem.networkSettings.internalConnection',
  );
  const tCommon = useTranslations('designSystem.common');
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error: unknown) {
      console.error(
        new Error('Failed to copy value to clipboard', { cause: error }),
      );
      toasterApi.error({
        title: tCommon('error'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }, [tCommon, value]);

  return (
    <Field.Root w="full">
      <Field.Label>{label}</Field.Label>
      <HStack gap={1} alignItems="center" w="full">
        <Input
          readOnly
          value={value}
          type={visible ? 'text' : 'password'}
          fontFamily="mono"
          fontSize="xs"
          flex={1}
          minW={0}
        />
        <Tooltip title={visible ? hideAriaLabel : showAriaLabel}>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={visible ? hideAriaLabel : showAriaLabel}
            onClick={() => setVisible((current) => !current)}
            flexShrink={0}
          >
            {visible ? <ViewOffIcon size={16} /> : <ViewIcon size={16} />}
          </IconButton>
        </Tooltip>
        <Tooltip title={copied ? tAdmin('copied') : copyAriaLabel}>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={copyAriaLabel}
            onClick={() => void handleCopy()}
            flexShrink={0}
          >
            {copied ? <Tick01Icon size={16} /> : <Copy01Icon size={16} />}
          </IconButton>
        </Tooltip>
      </HStack>
    </Field.Root>
  );
}
